import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Bell, Cake, CalendarDays, ChevronDown, Plane, Users } from 'lucide-react'
import api from '../services/api'
import FuncionarioFeriasModal from '../components/FuncionarioFeriasModal'
import { useEmpresa } from '../contexts/EmpresaContext'

// Dashboard de Funcionários (layout do Leandro). Período e estabelecimento valem pro dashboard todo; cada bloco é uma seção abaixo.
// Por enquanto só Ocorrências e acompanhamento (frota.ocomot).

interface GrupoExame { funcionarios: number; exames: number; inicio: string; fim: string }
interface GrupoCnh { funcionarios: number; inicio: string; fim: string }
type GrupoFerias = 'vencidas' | 'ate90' | 'ate180' | 'emFerias' | 'programadas'
interface LinhaFerias {
  motcod: string; nome: string; cargo: string; status: 'VENCIDA' | 'A_VENCER' | 'NO_PRAZO' | 'PROGRAMADA' | 'EM_DIA'
  periodoIni: string; periodoFim: string; direito: number; gozados: number; vendidos: number; saldo: number; limite: string; diasLimite: number
  gozoAtual: { ini: string; fim: string } | null; programado: { ini: string; fim: string } | null; grupos: GrupoFerias[]
}
interface PainelFerias { hoje: string; kpis: Record<GrupoFerias, number>; linhas: LinhaFerias[] }
interface ContratoExp { motcod: string; nome: string; cargo: string; empresa: number | null; admissao: string; vence: string }
interface PainelExperiencia { hoje: string; alertaDias: number; contratos: ContratoExp[]; semData: { motcod: string; nome: string; cargo: string; empresa: number | null; admissao: string }[] }
interface DataComemorativa { motcod: string; nome: string; cargo: string; fone: string; data: string; anos: number; origem: string }
interface PainelDatas { hoje: string; diasPassados: number; diasProximos: number; semanaPassada: DataComemorativa[]; proximos: DataComemorativa[]; contratacao: DataComemorativa[] }
interface ItemContagem { nome: string; qtd: number }
interface MovimentoPessoa { motcod: string; nome: string; cargo: string; empresa: number | null; data: string; obs: string; motivo: string }
interface PainelQuadro {
  inicio: string; fim: string; ativos: number; variacao: number; admissoes: number; demissoes: number
  tempoMedioAnos: number
  afastados: { total: number; percentual: number; porMotivo: ItemContagem[] }
  admissoesPorCargo: { total: number; itens: ItemContagem[]; lista: MovimentoPessoa[] }
  demissoesPorMotivo: { total: number; itens: ItemContagem[]; lista: MovimentoPessoa[] }
}
interface Pessoa { motcod: string; nome: string; cargo: string; empresa: number | null; risco: 'ALTO' | 'ATENCAO'; motivos: string[] }
interface Ocorrencias {
  inicio: string; fim: string
  afastamentosAtivos: number
  exames: { vencido: GrupoExame; esteMes: GrupoExame; proxMes: GrupoExame }
  cnh: { vencida: GrupoCnh; esteMes: GrupoCnh; proxMes: GrupoCnh }
  registros: { vencido: GrupoExame; esteMes: GrupoExame; proxMes: GrupoExame }
  ferias: { total: number; vencidas: number; aVencer: number; avisoDias: number }
  resumo: { andamento: number; concluidas: number; advertencias: number; suspensoes: number; total: number }
  porNatureza: { nat: string; nome: string; qtd: number }[]
  evolucao: { mes: string; andamento: number; concluidas: number }[]
  analise: Pessoa[]
  regras: Record<'faltas' | 'advertencias' | 'atestados', { atencao: number; alto: number; janela: string }>
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const rotuloMes = (m: string) => `${MESES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}`
const COR = { andamento: '#df3b46', concluidas: '#268a56', advert: '#ff7600', susp: '#7458b7', total: '#1477ad' }
const LARGURA = 1306

function periodoPreset(tipo: '7dias' | 'mes' | 'anterior'): [string, string] {
  const hoje = new Date()
  if (tipo === '7dias') { const i = new Date(hoje); i.setDate(hoje.getDate() - 6); return [iso(i), iso(hoje)] }
  if (tipo === 'mes') return [iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), iso(hoje)]
  return [iso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)), iso(new Date(hoje.getFullYear(), hoje.getMonth(), 0))]
}

// Seções do dashboard, na ordem em que aparecem: a barra de navegação e o cabeçalho de cada bloco usam esta lista
const SECOES = [
  { id: 'bloco-acoes', rot: 'Ações necessárias' }, { id: 'bloco-quadro', rot: 'Quadro e movimentações' }, { id: 'bloco-ferias', rot: 'Férias' },
  { id: 'bloco-experiencia', rot: 'Contratos de experiência' }, { id: 'bloco-ocorrencias', rot: 'Ocorrências' }, { id: 'bloco-datas', rot: 'Datas comemorativas' },
]
const CHAVE_RECOLHIDAS = 'dashFuncionarios.recolhidas'
const lerRecolhidas = (): string[] => { try { return JSON.parse(localStorage.getItem(CHAVE_RECOLHIDAS) || '[]') } catch { return [] } }
const abrirSecao =(id: string) => window.dispatchEvent(new CustomEvent('dash-abrir-secao', { detail: id }))

// Cabeçalho padrão de cada bloco: ícone, título, descrição, ação opcional e botão de recolher (a escolha fica lembrada neste navegador)
function SecaoDash({ id, icone, titulo, sub, acao, children }: { id: string; icone: React.ReactNode; titulo: string; sub?: string; acao?: React.ReactNode; children: React.ReactNode }) {
  const [aberta, setAberta] = useState(() => !lerRecolhidas().includes(id))
  const definir = (v: boolean) => {
    setAberta(v)
    try { localStorage.setItem(CHAVE_RECOLHIDAS, JSON.stringify([...lerRecolhidas().filter(x => x !== id), ...(v ? [] : [id])])) } catch { /* sem armazenamento */ }
  }
  useEffect(() => { // botões como "Ver férias" abrem a seção se ela estiver recolhida
    const abrir = (e: Event) => { if ((e as CustomEvent).detail === id) definir(true) }
    window.addEventListener('dash-abrir-secao', abrir)
    return () => window.removeEventListener('dash-abrir-secao', abrir)
  }) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section id={id} style={{ maxWidth: LARGURA, width: '100%', scrollMarginTop: 52 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid #dce5ea', marginBottom: aberta ? 10 : 0 }}>
        <button type="button" onClick={() => definir(!aberta)} title={aberta ? 'Recolher esta seção' : 'Expandir esta seção'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}>
          <span style={{ width: 25, height: 25, borderRadius: 7, background: '#e5f1f7', display: 'grid', placeItems: 'center', color: '#126f9f' }}>{icone}</span>
          <h3 style={{ margin: 0, fontSize: 12.5, color: '#304e63', letterSpacing: 0.35, textAlign: 'left' }}>{titulo}</h3>
          <ChevronDown size={15} color="#81919f" style={{ transform: aberta ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
        </button>
        {sub && <small style={{ marginLeft: 'auto', color: '#526f83', fontSize: 11, fontWeight: 600 }}>{sub}</small>}
        {acao && <span style={{ marginLeft: sub ? 0 : 'auto' }}>{acao}</span>}
      </div>
      {aberta && children}
    </section>
  )
}

// Atalhos para pular entre os blocos; fica visível no topo ao rolar a página
function NavegacaoDash({ ids }: { ids: string[] }) {
  return (
    <nav style={{ position: 'sticky', top: 0, zIndex: 5, maxWidth: LARGURA, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '6px 8px', background: '#f4f8fbee', border: '1px solid #dce5ea', borderRadius: 10, backdropFilter: 'blur(4px)' }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: '#81919f', letterSpacing: 0.4, marginRight: 4 }}>IR PARA</span>
      {SECOES.filter(s => ids.includes(s.id)).map(s => (
        <button key={s.id} type="button" onClick={() => { abrirSecao(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }}
          style={{ border: '1px solid #cfdde6', background: '#fff', color: '#176b99', borderRadius: 999, padding: '3px 11px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{s.rot}</button>
      ))}
    </nav>
  )
}

function Cartao({ rot, valor, det, cor }: { rot: string; valor: number | string; det: string; cor: string }) {
  return (
    <div style={{ position: 'relative', background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, padding: '9px 12px 9px 14px', boxShadow: '0 2px 6px #193c5210' }}>
      <span style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 4, background: cor, borderRadius: '0 4px 4px 0' }} />
      <div style={{ fontSize: 10, fontWeight: 800, color: '#526f83', letterSpacing: 0.3 }}>{rot}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#10303f', lineHeight: 1.15, margin: '3px 0 1px' }}>{valor}</div>
      <div style={{ fontSize: 10.5, color: '#81919f' }}>{det}</div>
    </div>
  )
}

// Painel "Ações necessárias" do HTML: cada pendência é um cartão pequeno com botão Analisar (por enquanto só Ocorrências)
function CartaoAcao({ rot, valor, texto, cor, onAnalisar, detalhe, botao = 'Analisar' }:
  { rot: string; valor: number; texto: string; cor: string; onAnalisar: () => void; detalhe?: string; botao?: string }) {
  return (
    <div style={{ width: 210, border: '1px solid #dce6eb', borderRadius: 12, background: '#fff', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#536b7b', letterSpacing: 0.35 }}>{rot}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <i style={{ width: 9, height: 9, borderRadius: '50%', background: cor, flex: 'none' }} />
        <b style={{ fontSize: 22, color: '#173f5f', lineHeight: 1 }}>{valor}</b>
        <span style={{ fontSize: 12, color: '#344b5b' }}>{texto}</span>
      </div>
      {detalhe && <div style={{ fontSize: 10, color: '#718592' }}>{detalhe}</div>}
      <button type="button" onClick={onAnalisar}
        style={{ alignSelf: 'flex-start', border: '1px solid #a9cadb', background: '#fff', color: '#176b99', borderRadius: 8, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
        {botao}
      </button>
    </div>
  )
}

// Cartão de vencimentos (exames, CNH): funcionários (ativos e não afastados) vencendo este mês, no próximo e já vencidos; o mouse mostra o detalhe
interface GrupoVenc { qtd: number; inicio: string; fim: string; dica: string }
function CartaoVencimentos({ rot, esteMes, proxMes, vencido, botao, onAbrir }:
  { rot: string; esteMes: GrupoVenc; proxMes: GrupoVenc; vencido: GrupoVenc; botao: string; onAbrir: (inicio: string, fim: string) => void }) {
  const grupos: { rot: string; g: GrupoVenc; vencido?: boolean }[] = [
    { rot: 'Este mês', g: esteMes }, { rot: 'Próximo mês', g: proxMes }, { rot: 'Vencido', g: vencido, vencido: true },
  ]
  return (
    <div style={{ width: 300, border: '1px solid #dce6eb', borderRadius: 12, background: '#fff', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#536b7b', letterSpacing: 0.35 }}>{rot}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {grupos.map(({ rot: r, g, vencido: ven }) => (
          <button key={r} type="button" onClick={() => onAbrir(g.inicio, g.fim)} title={g.dica}
            style={{ textAlign: 'left', cursor: 'pointer', padding: '7px 8px', borderRadius: 9,
              background: ven && g.qtd ? '#fff0f2' : '#edf5f8', border: `1px solid ${ven && g.qtd ? '#ffc3cb' : '#d8e7ee'}` }}>
            <b style={{ display: 'block', fontSize: 20, lineHeight: 1, color: ven && g.qtd ? '#c53243' : '#176b99' }}>{g.qtd}</b>
            <small style={{ display: 'block', marginTop: 4, fontSize: 10, fontWeight: 600, color: ven && g.qtd ? '#c53243' : '#5e7685' }}>{r}</small>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 10, color: '#718592' }}>Ativos e não afastados</div>
      <button type="button" onClick={() => onAbrir(esteMes.inicio, proxMes.fim)}
        style={{ alignSelf: 'flex-start', border: '1px solid #a9cadb', background: '#fff', color: '#176b99', borderRadius: 8, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
        {botao}
      </button>
    </div>
  )
}

const vencExame = (g: GrupoExame): GrupoVenc => ({ qtd: g.funcionarios, inicio: g.inicio, fim: g.fim, dica: `${g.funcionarios} funcionário(s) · ${g.exames} exame(s)` })
const vencRegistro = (g: GrupoExame): GrupoVenc => ({ qtd: g.funcionarios, inicio: g.inicio, fim: g.fim, dica: `${g.funcionarios} funcionário(s) · ${g.exames} registro(s)/certificado(s)` })
const vencCnh =(g: GrupoCnh): GrupoVenc => ({ qtd: g.funcionarios, inicio: g.inicio, fim: g.fim, dica: `${g.funcionarios} funcionário(s)` })

function AcoesNecessarias({ ocorrencias, afastamentos, exames, cnh, registros, ferias, onAnalisarOcorrencias, onAnalisarPessoas, onAbrirExames, onAbrirCnh, onAbrirRegistros, onVerFerias }:
  { ocorrencias: number; afastamentos: number; exames: Ocorrencias['exames']; cnh: Ocorrencias['cnh']; registros: Ocorrencias['registros']; ferias: Ocorrencias['ferias']; onAnalisarOcorrencias: () => void; onAnalisarPessoas: () => void;
    onAbrirExames: (inicio: string, fim: string) => void; onAbrirCnh: (inicio: string, fim: string) => void; onAbrirRegistros: (inicio: string, fim: string) => void; onVerFerias: () => void }) {
  return (
    <section id="bloco-acoes" style={{ width: 'fit-content', maxWidth: LARGURA, minWidth: 300, scrollMarginTop: 52, border: '1px solid #d9e3ea', borderRadius: 14, overflow: 'hidden', background: '#fff', boxShadow: '0 6px 18px rgba(32,59,78,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'linear-gradient(135deg, #0d5f90, #1682b7)', color: '#fff' }}>
        <Bell size={16} />
        <strong style={{ fontSize: 14 }}>Ações necessárias</strong>
        <span style={{ marginLeft: 'auto', background: '#fff', color: '#b3202f', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
          {ocorrencias} pendência{ocorrencias === 1 ? '' : 's'} prioritária{ocorrencias === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: 12, background: '#f8fafb' }}>
        <CartaoAcao rot="OCORRÊNCIAS" valor={ocorrencias} texto="em andamento" cor="#ff5362" onAnalisar={onAnalisarOcorrencias} />
        <CartaoVencimentos rot="EXAMES MÉDICOS" botao="Agendar renovação" onAbrir={onAbrirExames}
          esteMes={vencExame(exames.esteMes)} proxMes={vencExame(exames.proxMes)} vencido={vencExame(exames.vencido)} />
        <CartaoVencimentos rot="CNH" botao="Ver vencimentos" onAbrir={onAbrirCnh}
          esteMes={vencCnh(cnh.esteMes)} proxMes={vencCnh(cnh.proxMes)} vencido={vencCnh(cnh.vencida)} />
        <CartaoVencimentos rot="REGISTROS E CERTIFICADOS" botao="Atualizar registros" onAbrir={onAbrirRegistros}
          esteMes={vencRegistro(registros.esteMes)} proxMes={vencRegistro(registros.proxMes)} vencido={vencRegistro(registros.vencido)} />
        <CartaoAcao rot="FÉRIAS" valor={ferias.total} texto="exigem programação" cor="#f2aa20" botao="Ver férias" onAnalisar={onVerFerias}
          detalhe={`${ferias.vencidas} vencida${ferias.vencidas === 1 ? '' : 's'} · ${ferias.aVencer} a vencer em ${ferias.avisoDias} dias`} />
        <CartaoAcao rot="PESSOAS" valor={afastamentos} texto={afastamentos === 1 ? 'afastamento ativo' : 'afastamentos ativos'} cor="#73c6ee" onAnalisar={onAnalisarPessoas} />
      </div>
    </section>
  )
}

// Visão do quadro, movimentações e afastamentos: 3 indicadores e 3 cartões com lista (afastados, admissões por cargo, demissões por motivo)

function CartaoLista({ rot, total, sub, cor, fundo, itens, link, onLink }:
  { rot: string; total: number; sub: string; cor: string; fundo: string; itens: ItemContagem[]; link: string; onLink: () => void }) {
  return (
    <div style={{ position: 'relative', display: 'flex', background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, padding: '12px 14px 10px 18px', boxShadow: '0 2px 6px #193c5210', minWidth: 0 }}>
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: cor, borderRadius: '10px 0 0 10px' }} />
      <div style={{ width: 110, flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', borderRight: '1px solid #e4ebf0', paddingRight: 10 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, color: '#526f83', letterSpacing: 0.3 }}>{rot}</div>
        <div style={{ fontSize: 30, fontWeight: 800, color: '#10303f', lineHeight: 1.2, margin: '4px 0 2px' }}>{total}</div>
        <div style={{ fontSize: 10.5, color: '#81919f' }}>{sub}</div>
        <i style={{ width: 34, height: 3, borderRadius: 2, background: cor, opacity: 0.6, marginTop: 8 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingLeft: 10, display: 'flex', flexDirection: 'column' }}>
        {itens.length === 0 && <div style={{ fontSize: 11, color: '#94a3b8', padding: '14px 0' }}>Nenhum registro.</div>}
        {itens.map(it => (
          <div key={it.nome} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eef2f5' }}>
            <b style={{ flex: 'none', minWidth: 22, textAlign: 'center', borderRadius: 5, padding: '1px 5px', fontSize: 11.5, background: fundo, color: cor }}>{it.qtd}</b>
            <span style={{ fontSize: 11.5, color: '#344b5b', lineHeight: 1.25 }}>{it.nome}</span>
          </div>
        ))}
        <button type="button" onClick={onLink} style={{ alignSelf: 'flex-end', marginTop: 'auto', paddingTop: 6, border: 0, background: 'none', color: '#176b99', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{link}</button>
      </div>
    </div>
  )
}

function ListaMovimento({ titulo, lista, comMotivo, onFechar }: { titulo: string; lista: MovimentoPessoa[]; comMotivo: boolean; onFechar: () => void }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <b style={{ fontSize: 12.5, color: '#10303f' }}>{titulo} ({lista.length})</b>
        <button type="button" onClick={onFechar} style={{ marginLeft: 'auto', border: 0, background: 'none', color: '#176b99', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Fechar</button>
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead><tr style={{ textAlign: 'left', color: '#526f83', fontSize: 10 }}>
            <th style={{ padding: '3px 6px' }}>DATA</th><th style={{ padding: '3px 6px' }}>FUNCIONÁRIO</th><th style={{ padding: '3px 6px' }}>CARGO</th>{comMotivo && <th style={{ padding: '3px 6px' }}>MOTIVO / OBSERVAÇÃO</th>}
          </tr></thead>
          <tbody>
            {lista.map((p, i) => (
              <tr key={p.motcod + p.data + i} style={{ borderTop: '1px solid #eef2f5' }}>
                <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{dataBr(p.data)}</td>
                <td style={{ padding: '3px 6px', fontWeight: 600 }}>{p.nome}</td>
                <td style={{ padding: '3px 6px' }}>{p.cargo}</td>
                {comMotivo && <td style={{ padding: '3px 6px', color: '#64748b' }}>{p.motivo && <b style={{ color: '#334155' }}>{p.motivo}</b>}{p.motivo && p.obs ? ' · ' : ''}{p.obs}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BlocoQuadro({ q, onAfastados }: { q: PainelQuadro; onAfastados: () => void }) {
  const [aberta, setAberta] = useState<'' | 'adm' | 'dem'>('')
  return (
    <SecaoDash id="bloco-quadro" icone={<Users size={14} />} titulo="VISÃO DO QUADRO, MOVIMENTAÇÕES E AFASTAMENTOS" sub="Indicadores do quadro, entradas, saídas e situações de afastamento">
     <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        <Cartao rot="FUNCIONÁRIOS ATIVOS" valor={q.ativos} cor="#1477ad"
          det={`${q.variacao > 0 ? '+' : ''}${q.variacao} no período`} />
        <Cartao rot="TEMPO MÉDIO DE EMPRESA" valor={`${q.tempoMedioAnos.toLocaleString('pt-BR')} anos`} cor="#1477ad" det="Média dos funcionários ativos" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
        <CartaoLista rot="AFASTADOS" total={q.afastados.total} sub={`${q.afastados.percentual.toLocaleString('pt-BR')}% dos ativos`} cor="#f2aa20" fundo="#fff3dc"
          itens={q.afastados.porMotivo} link="Ver todos os afastamentos" onLink={onAfastados} />
        <CartaoLista rot="ADMISSÕES POR CARGO" total={q.admissoesPorCargo.total} sub="admissões no período" cor="#268a56" fundo="#e2f3ea"
          itens={q.admissoesPorCargo.itens} link="Ver todas as admissões" onLink={() => setAberta(aberta === 'adm' ? '' : 'adm')} />
        <CartaoLista rot="DEMISSÕES POR MOTIVO" total={q.demissoesPorMotivo.total} sub="demissões no período" cor="#df3b46" fundo="#fde8ea"
          itens={q.demissoesPorMotivo.itens} link="Ver todas as demissões" onLink={() => setAberta(aberta === 'dem' ? '' : 'dem')} />
      </div>
      {aberta === 'adm' && <ListaMovimento titulo="Admissões no período" lista={q.admissoesPorCargo.lista} comMotivo={false} onFechar={() => setAberta('')} />}
      {aberta === 'dem' && <ListaMovimento titulo="Demissões no período" lista={q.demissoesPorMotivo.lista} comMotivo onFechar={() => setAberta('')} />}
     </div>
    </SecaoDash>
  )
}

// Datas comemorativas: aniversariantes (semana passada e próximos) e aniversários de contratação; "Ver os N" abre a lista toda
function LinhaData({ d, hoje, contratacao }: { d: DataComemorativa; hoje: string; contratacao?: boolean }) {
  const ehHoje = d.data === hoje
  const etiqueta = ehHoje
    ? <span style={{ fontSize: 11, fontWeight: 800, color: '#c53243', background: '#ffe9ec', border: '1px solid #ffc3cb', borderRadius: 999, padding: '2px 9px' }}>Hoje</span>
    : <span style={{ fontSize: 11, fontWeight: 600, color: '#5e7685', background: '#edf2f5', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{d.data.slice(8)}/{d.data.slice(5, 7)}</span>
  if (contratacao) { // painel estreito: nome e etiqueta em cima, detalhes embaixo
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', alignItems: 'center', columnGap: 8, rowGap: 2, padding: '6px 4px', borderBottom: '1px solid #eef2f5', background: ehHoje ? '#fff8dd' : 'transparent', borderRadius: ehHoje ? 6 : 0 }}>
        <b style={{ fontSize: 13, color: '#152f43' }}>{d.data.slice(8)}</b>
        <span style={{ fontSize: 12.5, fontWeight: ehHoje ? 800 : 600, color: '#152f43', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nome}</span>
        {etiqueta}
        <span />
        <span style={{ gridColumn: '2 / 4', display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11, color: '#718592' }}>
          {d.fone && <span style={{ color: '#1675a8' }}>📱 {d.fone}</span>}
          <span>{d.anos} {d.anos === 1 ? 'ano' : 'anos'} de empresa</span>
          <b style={{ color: '#176b99' }}>Admissão: {dataBr(d.origem)}</b>
        </span>
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.9fr) auto', alignItems: 'center', gap: 8, padding: '6px 4px', borderBottom: '1px solid #eef2f5', background: ehHoje ? '#fff8dd' : 'transparent', borderRadius: ehHoje ? 6 : 0 }}>
      <b style={{ fontSize: 13, color: '#152f43' }}>{d.data.slice(8)}</b>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: ehHoje ? 800 : 600, color: '#152f43', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nome}</span>
        <span style={{ flex: 'none', fontSize: 10, fontWeight: 700, color: '#176b99', background: '#e6f2f8', borderRadius: 999, padding: '1px 6px' }}>{d.anos} anos</span>
      </span>
      <span style={{ fontSize: 11.5, color: '#1675a8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.fone && `📱 ${d.fone}`}</span>
      <span style={{ fontSize: 11.5, color: '#718592', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.cargo}</span>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {etiqueta}
      </span>
    </div>
  )
}

function BlocoDatas({ p }: { p: PainelDatas }) {
  const [todosAniv, setTodosAniv] = useState(false)
  const [todosContr, setTodosContr] = useState(false)
  const MAX = 3
  const totalAniv = p.semanaPassada.length + p.proximos.length
  const passada = todosAniv ? p.semanaPassada : p.semanaPassada.slice(0, MAX)
  const proximos = todosAniv ? p.proximos : p.proximos.slice(0, MAX)
  const contr = todosContr ? p.contratacao : p.contratacao.slice(0, MAX)
  const subtitulo = (t: string, extra?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, color: '#526f83', background: '#eef5f9', borderRadius: 6, padding: '4px 8px', margin: '6px 0 2px' }}>
      {t}{extra && <span style={{ marginLeft: 'auto', fontWeight: 500, color: '#81919f' }}>{extra}</span>}
    </div>
  )
  const link = (aberto: boolean, set: (v: boolean) => void, texto: string) => (
    <div style={{ textAlign: 'right', paddingTop: 6 }}>
      <button type="button" onClick={() => set(!aberto)} style={{ border: 0, background: 'none', color: '#176b99', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{aberto ? 'Mostrar menos' : texto}</button>
    </div>
  )
  return (
    <SecaoDash id="bloco-datas" icone={<Cake size={14} />} titulo="DATAS COMEMORATIVAS" sub="Aniversários pessoais e de contratação">
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 1fr)', background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, boxShadow: '0 2px 6px #193c5210', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderRight: '1px solid #e4ebf0', minWidth: 0 }}>
          <b style={{ fontSize: 12, color: '#10303f' }}>🎂 ANIVERSARIANTES</b>
          {passada.length > 0 && <>
            {subtitulo('↩ Semana passada', 'Inclui finais de semana e feriados')}
            {passada.map(d => <LinhaData key={d.motcod + d.data} d={d} hoje={p.hoje} />)}
          </>}
          {subtitulo(`Hoje e próximos ${p.diasProximos} dias`)}
          {proximos.length === 0 && <div style={{ fontSize: 11.5, color: '#94a3b8', padding: '10px 4px' }}>Nenhum aniversariante nos próximos dias.</div>}
          {proximos.map(d => <LinhaData key={d.motcod + d.data} d={d} hoje={p.hoje} />)}
          {totalAniv > MAX && link(todosAniv, setTodosAniv, `Ver os ${totalAniv} aniversariantes`)}
        </div>
        <div style={{ padding: '10px 14px', minWidth: 0 }}>
          <b style={{ fontSize: 12, color: '#10303f' }}>🏅 ANIVERSÁRIOS DE CONTRATAÇÃO</b>
          {contr.length === 0 && <div style={{ fontSize: 11.5, color: '#94a3b8', padding: '10px 4px' }}>Nenhum aniversário de contratação nos próximos dias.</div>}
          {contr.map(d => <LinhaData key={d.motcod + d.data} d={d} hoje={p.hoje} contratacao />)}
          {p.contratacao.length > MAX && link(todosContr, setTodosContr, `Ver os ${p.contratacao.length} aniversários`)}
        </div>
      </div>
    </SecaoDash>
  )
}

function Grafico({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #d4e2ec', borderRadius: 11, padding: 12, minWidth: 0, minHeight: 215 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 12.5, color: '#10303f' }}>{titulo}</h3>
      {children}
    </div>
  )
}

const FERIAS_GRUPOS: { id: GrupoFerias; rot: string; sub: string; cor: string; titulo: string; descricao: string }[] = [
  { id: 'vencidas', rot: 'Férias vencidas', sub: 'Ação imediata', cor: '#df3b46', titulo: 'FÉRIAS VENCIDAS', descricao: 'Funcionários que exigem ação imediata' },
  { id: 'ate90', rot: 'Vence em até 90 dias', sub: 'Programar agora', cor: '#efa426', titulo: 'VENCEM EM ATÉ 90 DIAS', descricao: 'Programar agora' },
  { id: 'ate180', rot: 'Vence nos próximos 180 dias', sub: 'Planejamento de curto prazo', cor: '#126f9f', titulo: 'VENCEM NOS PRÓXIMOS 180 DIAS', descricao: 'Planejamento de curto prazo' },
  { id: 'emFerias', rot: 'Funcionários em férias', sub: 'Em gozo neste momento', cor: '#268a56', titulo: 'FUNCIONÁRIOS EM FÉRIAS', descricao: 'Em gozo neste momento' },
  { id: 'programadas', rot: 'Férias programadas', sub: 'Acompanhar concessão', cor: '#7458b7', titulo: 'FÉRIAS PROGRAMADAS', descricao: 'Acompanhar concessão' },
]
const STATUS_FERIAS: Record<LinhaFerias['status'], { txt: string; fg: string; bg: string }> = {
  VENCIDA: { txt: 'Vencida', fg: '#991b1b', bg: '#fee2e2' }, A_VENCER: { txt: 'A vencer', fg: '#92400e', bg: '#fef3c7' },
  NO_PRAZO: { txt: 'No prazo', fg: '#1f2937', bg: '#f1f5f9' }, PROGRAMADA: { txt: 'Programada', fg: '#1e40af', bg: '#dbeafe' }, EM_DIA: { txt: 'Em dia', fg: '#166534', bg: '#dcfce7' },
}
const dataBr = (d: string) => (d ? d.split('-').reverse().join('/') : '')
const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const iniciais = (nome: string) => nome.trim().split(/\s+/).filter(p => p.length > 2).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?'

// Controle de férias (versão enxuta do HTML): cartões clicáveis + lista. Direito fixo de 30 dias; "Agendar férias" abre o lançamento de férias do funcionário.
function BlocoFerias({ painel, grupo, setGrupo, onAgendar, onCompleto }:
  { painel: PainelFerias; grupo: GrupoFerias; setGrupo: (g: GrupoFerias) => void; onAgendar: (l: LinhaFerias) => void; onCompleto: () => void }) {
  const [busca, setBusca] = useState('')
  const info = FERIAS_GRUPOS.find(g => g.id === grupo)!
  const q = semAcento(busca.trim())
  const linhas = painel.linhas.filter(l => l.grupos.includes(grupo)).filter(l => !q || semAcento(l.nome).includes(q))
  const cel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 1, fontSize: 10, color: '#81919f' }
  const val: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#10303f' }
  const colunas = '34px minmax(160px, 1.3fr) 150px 70px 70px 70px 135px 85px 105px'

  return (
    <SecaoDash id="bloco-ferias" icone={<Plane size={14} />} titulo="CONTROLE DE FÉRIAS" sub="Período aquisitivo, limite concessivo, saldo e programação"
      acao={<button className="btn btn-outline btn-sm" onClick={onCompleto}>Controle de Férias completo</button>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #d9e5eb', borderRadius: 10, padding: '7px 10px', marginBottom: 8 }}>
        <b style={{ fontSize: 11.5, color: '#304e63', whiteSpace: 'nowrap' }}>Pesquisar funcionário</b>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Digite o nome do funcionário..." style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px' }} />
        <button className="btn btn-outline btn-sm" onClick={() => setBusca('')}>Limpar</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginBottom: 8 }}>
        {FERIAS_GRUPOS.map(g => {
          const ativo = g.id === grupo
          return (
            <button key={g.id} type="button" onClick={() => setGrupo(g.id)}
              style={{ position: 'relative', textAlign: 'left', cursor: 'pointer', background: '#fff', borderRadius: 10, padding: '9px 12px 9px 15px',
                border: ativo ? `2px solid ${g.cor}` : '1px solid #dfe7ec', boxShadow: ativo ? `0 0 0 2px ${g.cor}22` : '0 2px 6px #193c5210' }}>
              <span style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 4, background: g.cor, borderRadius: '0 4px 4px 0' }} />
              <b style={{ display: 'block', fontSize: 24, lineHeight: 1.1, color: g.cor }}>{painel.kpis[g.id]}</b>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#10303f', margin: '2px 0 1px' }}>{g.rot}</span>
              <small style={{ fontSize: 10, color: '#81919f' }}>{g.sub}</small>
            </button>
          )
        })}
      </div>

      <div style={{ background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10 }}>
        <div style={{ padding: '9px 14px', borderBottom: '1px solid #edf1f3' }}>
          <b style={{ fontSize: 12, color: '#304e63', letterSpacing: 0.3 }}>{info.titulo}</b>
          <div style={{ fontSize: 10.5, color: '#81919f' }}>{info.descricao} · {linhas.length} funcionário{linhas.length === 1 ? '' : 's'}</div>
        </div>
        {linhas.length === 0 && <div style={{ padding: '14px', fontSize: 12, color: '#94a3b8' }}>Nenhum funcionário nesta situação{q ? ' com esse nome' : ''}.</div>}
        <div style={{ maxHeight: 330, overflow: 'auto' }}>
          {linhas.map(l => {
            const st = grupo === 'emFerias' ? { txt: 'Em gozo', fg: '#166534', bg: '#dcfce7' } : grupo === 'programadas' ? STATUS_FERIAS.PROGRAMADA : STATUS_FERIAS[l.status]
            const gozo = grupo === 'emFerias' ? l.gozoAtual : grupo === 'programadas' ? l.programado : null
            return (
              <div key={l.motcod} style={{ display: 'grid', gridTemplateColumns: colunas, gap: 10, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid #edf1f3', minWidth: 880 }}>
                <span style={{ width: 30, height: 30, borderRadius: '50%', background: '#e7f3f8', color: '#126f9f', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800 }}>{iniciais(l.nome)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#10303f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.nome}</div>
                  <div style={{ fontSize: 10.5, color: '#81919f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.cargo}</div>
                </div>
                <div style={cel}>Período aquisitivo<span style={val}>{dataBr(l.periodoIni)} a {dataBr(l.periodoFim)}</span></div>
                <div style={cel}>Direito<span style={val}>{l.direito} dias</span></div>
                <div style={cel}>Já gozados<span style={val}>{l.gozados} dias</span></div>
                <div style={cel}>Em aberto<span style={val}>{l.saldo} dias</span></div>
                {gozo
                  ? <div style={cel}>{grupo === 'emFerias' ? 'Em gozo' : 'Período programado'}<span style={val}>{dataBr(gozo.ini)} a {dataBr(gozo.fim)}</span></div>
                  : <div style={cel}>Limite concessivo<span style={val}>{dataBr(l.limite)}</span></div>}
                <span style={{ justifySelf: 'start', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: st.fg, background: st.bg }}>{st.txt}</span>
                <button type="button" onClick={() => onAgendar(l)}
                  style={{ border: '1px solid #a9cadb', background: '#fff', color: '#176b99', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                  {grupo === 'programadas' ? 'Editar' : grupo === 'emFerias' ? 'Ver férias' : 'Agendar férias'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
      <div style={{ fontSize: 10, color: '#8293a1', marginTop: 6 }}>
        Só funcionários ativos, últimos 3 anos. Direito fixo de 30 dias (a redução por faltas, art. 130 da CLT, ainda não é considerada). Em aberto = direito − gozados − vendidos.
        O mesmo funcionário pode aparecer em mais de um grupo. "Agendar férias" abre o lançamento de férias do funcionário.
      </div>
    </SecaoDash>
  )
}

const MESES_LONGOS = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO']
const diasEntre = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86400000)

// Calendário de vencimentos dos contratos de experiência (campo "Experiência até"), com alerta de 7 dias
function BlocoExperiencia({ painel }: { painel: PainelExperiencia }) {
  const { hoje, alertaDias } = painel
  const limite = iso(new Date(Date.parse(`${hoje}T12:00:00`) + alertaDias * 86400000))
  const [mes, setMes] = useState(hoje.slice(0, 7))
  const [dia, setDia] = useState(() => painel.contratos.find(c => c.vence >= hoje)?.vence ?? hoje)
  const porDia = useMemo(() => {
    const m = new Map<string, ContratoExp[]>()
    painel.contratos.forEach(c => m.set(c.vence, [...(m.get(c.vence) ?? []), c]))
    return m
  }, [painel.contratos])
  const [ano, mm] = mes.split('-').map(Number)
  const desloc = new Date(ano, mm - 1, 1).getDay()
  const total = Math.ceil((desloc + new Date(ano, mm, 0).getDate()) / 7) * 7
  const trocarMes = (n: number) => { const d = new Date(ano, mm - 1 + n, 1); setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`) }
  const doDia = porDia.get(dia) ?? []
  const situacao = (v: string) => (v < hoje ? { cor: '#7b8794', bg: '#eef1f4' } : v <= limite ? { cor: '#df3b46', bg: '#fde8ea' } : { cor: '#1477ad', bg: '#e3f1f8' })
  const quando = (v: string) => { const n = diasEntre(hoje, v); return n === 0 ? 'vence hoje' : n > 0 ? `vence em ${n} dia${n > 1 ? 's' : ''}` : `venceu há ${-n} dia${n < -1 ? 's' : ''}` }
  const seta: React.CSSProperties = { width: 26, height: 26, border: '1px solid #cfdde6', background: '#fff', borderRadius: 7, cursor: 'pointer', fontSize: 14, color: '#304e63' }

  return (
    <SecaoDash id="bloco-experiencia" icone={<CalendarDays size={14} />} titulo="CONTRATOS DE EXPERIÊNCIA" sub={`Agenda de vencimentos com alerta antecipado de ${alertaDias} dias`}>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(280px, 1fr)', gap: 10, alignItems: 'start' }}>
        <div style={{ background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <b style={{ fontSize: 11.5, color: '#304e63' }}>📅 CALENDÁRIO DE VENCIMENTOS</b>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" style={seta} onClick={() => trocarMes(-1)} aria-label="Mês anterior">‹</button>
              <strong style={{ fontSize: 11.5, color: '#10303f', minWidth: 130, textAlign: 'center' }}>{MESES_LONGOS[mm - 1]} DE {ano}</strong>
              <button type="button" style={seta} onClick={() => trocarMes(1)} aria-label="Próximo mês">›</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 3 }}>
            {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map(s => <span key={s} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 800, color: '#81919f' }}>{s}</span>)}
            {Array.from({ length: total }, (_, n) => {
              const dt = new Date(ano, mm - 1, 1 - desloc + n), d = iso(dt)
              const lista = porDia.get(d) ?? [], fora = d.slice(0, 7) !== mes, st = lista.length ? situacao(d) : null
              return (
                <button key={d} type="button" disabled={!lista.length} onClick={() => setDia(d)}
                  style={{ minHeight: 46, textAlign: 'left', padding: '3px 5px', borderRadius: 7, cursor: lista.length ? 'pointer' : 'default', opacity: fora ? 0.45 : 1,
                    background: st ? st.bg : '#f8fafb', border: d === dia ? `2px solid ${st?.cor ?? '#1477ad'}` : d === hoje ? '2px solid #126f9f' : '1px solid #edf1f3' }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#10303f' }}>{dt.getDate()}</span>
                  {st && <span style={{ display: 'block', fontSize: 9, fontWeight: 700, color: st.cor, lineHeight: 1.15 }}>{lista.length} contrato{lista.length > 1 ? 's' : ''}</span>}
                </button>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 7, fontSize: 10, color: '#526f83' }}>
            <span><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#df3b46', marginRight: 4 }} />Próximos {alertaDias} dias</span>
            <span><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#1477ad', marginRight: 4 }} />Após {alertaDias} dias</span>
            <span><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#7b8794', marginRight: 4 }} />Já vencidos</span>
            <span><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, border: '2px solid #126f9f', marginRight: 4 }} />Data atual</span>
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10 }}>
          <div style={{ padding: '9px 14px', borderBottom: '1px solid #edf1f3' }}>
            <b style={{ fontSize: 12, color: '#304e63', letterSpacing: 0.3 }}>FUNCIONÁRIOS</b>
            <div style={{ fontSize: 10.5, color: '#81919f' }}>Vencimentos em {dataBr(dia)} · {doDia.length} contrato{doDia.length === 1 ? '' : 's'}</div>
          </div>
          <div style={{ maxHeight: 290, overflow: 'auto' }}>
            {doDia.length === 0 && <div style={{ padding: 14, fontSize: 12, color: '#94a3b8' }}>
              {painel.contratos.length ? 'Nenhum contrato vence nesse dia. Clique num dia marcado no calendário.' : 'Nenhum funcionário com "Experiência até" preenchido para os próximos meses.'}
            </div>}
            {doDia.map(c => {
              const st = situacao(c.vence)
              return (
                <div key={c.motcod} style={{ display: 'grid', gridTemplateColumns: '34px 1fr auto', gap: 10, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid #edf1f3' }}>
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: '#e7f3f8', color: '#126f9f', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800 }}>{iniciais(c.nome)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#10303f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</div>
                    <div style={{ fontSize: 10.5, color: '#81919f' }}>{c.cargo}{c.admissao ? ` · admitido em ${dataBr(c.admissao)}` : ''}</div>
                  </div>
                  <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: st.cor, background: st.bg, whiteSpace: 'nowrap' }}>{quando(c.vence)}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {painel.semData.length > 0 && (
        <details style={{ marginTop: 8, background: '#fff', border: '1px solid #dfe7ec', borderRadius: 10, padding: '8px 14px' }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#304e63' }}>
            {painel.semData.length} admitido{painel.semData.length > 1 ? 's' : ''} nos últimos 90 dias sem "Experiência até" preenchido
          </summary>
          <div style={{ maxHeight: 200, overflow: 'auto', marginTop: 6 }}>
            {painel.semData.map(s => (
              <div key={s.motcod} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: '1px solid #edf1f3', fontSize: 12 }}>
                <span><b style={{ color: '#10303f' }}>{s.nome}</b> <span style={{ color: '#81919f' }}>· {s.cargo}</span></span>
                <span style={{ color: '#81919f', whiteSpace: 'nowrap' }}>admitido em {dataBr(s.admissao)}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10, color: '#8293a1', marginTop: 6 }}>Preencha em Funcionários › cadastro › Complemento › "Experiência até" para o contrato entrar no calendário.</div>
        </details>
      )}
      <div style={{ fontSize: 10, color: '#8293a1', marginTop: 6 }}>Só funcionários ativos. O prazo do contrato (30, 45, 60 ou 90 dias) não é calculado: vale a data do campo "Experiência até".</div>
    </SecaoDash>
  )
}

function BlocoOcorrencias({ d, nomeEmpresa }: { d: Ocorrencias; nomeEmpresa: Map<number, string> }) {
  const navegar = useNavigate()
  const navigate = (p: string) => navegar(p, { state: { deDashboard: true } }) // as telas abertas daqui mostram o botão Voltar ao dashboard
  const r = d.resumo
  const maxMes = Math.max(1, ...d.evolucao.map(m => m.andamento + m.concluidas))
  const maxNat = Math.max(1, ...d.porNatureza.map(n => n.qtd))
  const legenda = (cor: string, txt: string) => <span><i style={{ display: 'inline-block', width: 8, height: 8, background: cor, borderRadius: 2, marginRight: 4 }} />{txt}</span>

  return (
    <SecaoDash id="bloco-ocorrencias" icone={<AlertTriangle size={14} />} titulo="OCORRÊNCIAS E ACOMPANHAMENTO" sub="Status, natureza e funcionários que exigem análise"
      acao={<button className="btn btn-outline btn-sm" onClick={() => navigate('/funcionarios/ocorrencias/lancamento')}>Abrir ocorrências</button>}>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 }}>
        <Cartao rot="EM ANDAMENTO" valor={r.andamento} det="Exigem análise" cor={COR.andamento} />
        <Cartao rot="CONCLUÍDAS" valor={r.concluidas} det={`${r.total ? Math.round((r.concluidas / r.total) * 100) : 0}% do total`} cor={COR.concluidas} />
        <Cartao rot="ADVERTÊNCIAS" valor={r.advertencias} det="No período" cor={COR.advert} />
        <Cartao rot="SUSPENSÕES" valor={r.suspensoes} det="No período" cor={COR.susp} />
        <Cartao rot="TOTAL" valor={r.total} det="No período" cor={COR.total} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
        <Grafico titulo="Evolução mensal das ocorrências">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
            {d.evolucao.map(m => {
              const tot = m.andamento + m.concluidas
              return (
                <div key={m.mes} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}
                  title={`${rotuloMes(m.mes)}: ${tot} (${m.andamento} em andamento, ${m.concluidas} concluídas)`}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#334155' }}>{tot || ''}</div>
                  <div style={{ width: '100%', maxWidth: 30, height: `${(tot / maxMes) * 84}px`, display: 'flex', flexDirection: 'column', borderRadius: '3px 3px 0 0', overflow: 'hidden' }}>
                    <div style={{ flex: m.andamento, background: COR.andamento }} />
                    <div style={{ flex: m.concluidas, background: COR.concluidas }} />
                  </div>
                  <div style={{ fontSize: 9.5, color: '#64748b', marginTop: 3 }}>{rotuloMes(m.mes)}</div>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: '#475569' }}>
            {legenda(COR.andamento, 'Em andamento')}{legenda(COR.concluidas, 'Concluídas')}
          </div>
        </Grafico>

        <Grafico titulo="Ocorrências por natureza">
          {d.porNatureza.length === 0 && <div style={{ color: '#94a3b8', fontSize: 11.5 }}>Nenhuma ocorrência no período.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 160, overflowY: 'auto' }}>
            {d.porNatureza.map(n => (
              <div key={n.nat || 'sem'} style={{ display: 'grid', gridTemplateColumns: '88px 1fr 28px', alignItems: 'center', gap: 6, fontSize: 11 }}>
                <span style={{ color: '#334155', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.nome}</span>
                <div style={{ background: '#eef2f7', borderRadius: 3, height: 11 }}>
                  <div style={{ width: `${(n.qtd / maxNat) * 100}%`, height: '100%', background: COR.total, borderRadius: 3 }} />
                </div>
                <b style={{ textAlign: 'right', color: '#10303f' }}>{n.qtd}</b>
              </div>
            ))}
          </div>
        </Grafico>

        <Grafico titulo="Funcionários que exigem análise">
          {d.analise.length === 0 && <div style={{ color: '#94a3b8', fontSize: 11.5 }}>Nenhum funcionário passou dos limites.</div>}
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            {d.analise.map(p => (
              <div key={p.motcod} style={{ padding: '6px 0', borderBottom: '1px solid #edf1f3', fontSize: 11.5 }}>
                <b>{p.nome}</b>
                <span style={{ float: 'right', padding: '2px 7px', borderRadius: 9, fontSize: 9, fontWeight: 800,
                  background: p.risco === 'ALTO' ? COR.andamento : '#f2aa20', color: '#fff' }}>{p.risco === 'ALTO' ? 'ALTO' : 'ATENÇÃO'}</span>
                <div style={{ color: '#81919f', fontSize: 10.5 }}>{[p.cargo, p.empresa != null ? nomeEmpresa.get(p.empresa) : ''].filter(Boolean).join(' · ')}</div>
                {p.motivos.map(m => <div key={m} style={{ color: '#475569', fontSize: 10.5 }}>{m}</div>)}
              </div>
            ))}
          </div>
        </Grafico>
      </div>

      <div style={{ fontSize: 10, color: '#8293a1', marginTop: 6 }}>
        Em andamento = ocorrência não concluída. Evolução: 6 meses até o fim do período. "Exigem análise": só funcionários ativos, contando a partir da data final —
        atenção com {d.regras.faltas.atencao}+ faltas em {d.regras.faltas.janela}, {d.regras.advertencias.atencao}+ advertências em {d.regras.advertencias.janela} ou {d.regras.atestados.atencao}+ atestados em {d.regras.atestados.janela}; ALTO com {d.regras.faltas.alto}+, {d.regras.advertencias.alto}+ e {d.regras.atestados.alto}+.
      </div>
    </SecaoDash>
  )
}

export default function DashboardFuncionarios() {
  const { empresas } = useEmpresa()
  const navegar = useNavigate()
  const navigate = (p: string) => navegar(p, { state: { deDashboard: true } }) // as telas abertas daqui mostram o botão Voltar ao dashboard
  const [ini0, fim0] = periodoPreset('mes')
  const [empresaSel, setEmpresaSel] = useState('')
  const [inicio, setInicio] = useState(ini0)
  const [fim, setFim] = useState(fim0)
  const [ocorrencias, setOcorrencias] = useState<Ocorrencias | null>(null)
  const [painelFerias, setPainelFerias] = useState<PainelFerias | null>(null)
  const [painelExp, setPainelExp] = useState<PainelExperiencia | null>(null)
  const [painelQuadro, setPainelQuadro] = useState<PainelQuadro | null>(null)
  const [painelDatas, setPainelDatas] = useState<PainelDatas | null>(null)
  const [grupoFerias, setGrupoFerias] = useState<GrupoFerias>('vencidas')
  const [modalFerias, setModalFerias] = useState<{ motcod: string; motnome: string } | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [atualizado, setAtualizado] = useState('')
  const [erro, setErro] = useState('')

  async function carregar(i = inicio, f = fim) {
    setErro(''); setCarregando(true)
    try {
      const [oc, fe, ex, qu, da] = await Promise.all([
        api.get('/ocorrencias-funcionarios/dashboard', { params: { inicio: i, fim: f, empresas: empresaSel } }),
        api.get('/funcionarios/relatorio/painel-ferias', { params: { empresas: empresaSel } }),
        api.get('/funcionarios/relatorio/painel-experiencia', { params: { empresas: empresaSel } }),
        api.get('/funcionarios/relatorio/painel-quadro', { params: { inicio: i, fim: f, empresas: empresaSel } }),
        api.get('/funcionarios/relatorio/painel-datas', { params: { empresas: empresaSel } }),
      ])
      setOcorrencias(oc.data); setPainelFerias(fe.data); setPainelExp(ex.data); setPainelQuadro(qu.data); setPainelDatas(da.data)
      setAtualizado(new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }))
    } catch (e: any) {
      setOcorrencias(null); setPainelFerias(null); setPainelExp(null); setPainelQuadro(null); setPainelDatas(null); setErro(e?.response?.data?.error || 'Erro ao carregar o dashboard.')
    } finally { setCarregando(false) }
  }

  // botão Ver férias do cartão: rola até o bloco, já nas vencidas (como no HTML)
  function verFerias() {
    setGrupoFerias('vencidas')
    abrirSecao('bloco-ferias')
    document.getElementById('bloco-ferias')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  useEffect(() => { carregar() }, [empresaSel]) // eslint-disable-line react-hooks/exhaustive-deps

  function preset(tipo: '7dias' | 'mes' | 'anterior') {
    const [i, f] = periodoPreset(tipo)
    setInicio(i); setFim(f); carregar(i, f)
  }

  const nomeEmpresa = useMemo(() => new Map(empresas.map(e => [e.empcod, e.empfan?.trim() || e.empnom?.trim() || String(e.empcod)])), [empresas])
  const campo: React.CSSProperties = { height: 30, padding: '0 0.5rem', fontSize: 12 }
  const btn: React.CSSProperties = { height: 30, padding: '0 0.7rem', fontSize: 11.5 }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap', maxWidth: LARGURA }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#10303f' }}>Dashboard de Funcionários</h2>
          <div style={{ fontSize: 11, color: '#81919f', marginTop: 2 }}>{atualizado ? `Atualizado em ${atualizado}` : 'Carregando...'}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
          <div className="field" style={{ width: 210, marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Estabelecimento</label>
            <select value={empresaSel} onChange={e => setEmpresaSel(e.target.value)} style={campo}>
              <option value="">Todos</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 128, marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>De</label>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={campo} />
          </div>
          <div className="field" style={{ width: 128, marginBottom: 0 }}>
            <label style={{ fontSize: 10 }}>Até</label>
            <input type="date" value={fim} onChange={e => setFim(e.target.value)} style={campo} />
          </div>
          <button className="btn btn-outline" style={btn} onClick={() => preset('7dias')}>7 dias</button>
          <button className="btn btn-outline" style={btn} onClick={() => preset('mes')}>Este mês</button>
          <button className="btn btn-outline" style={btn} onClick={() => preset('anterior')}>Mês anterior</button>
          <button className="btn btn-primary" style={btn} onClick={() => carregar()} disabled={carregando}>{carregando ? 'Atualizando...' : 'Atualizar'}</button>
        </div>
      </div>

      {erro && (
        <div style={{ maxWidth: LARGURA, fontSize: 12, fontWeight: 600, padding: '0.5rem 0.8rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
      )}

      {ocorrencias && <NavegacaoDash ids={SECOES.map(s => s.id)} />}

      {ocorrencias && (
        <AcoesNecessarias ocorrencias={ocorrencias.resumo.andamento} afastamentos={ocorrencias.afastamentosAtivos} exames={ocorrencias.exames} cnh={ocorrencias.cnh} registros={ocorrencias.registros} ferias={ocorrencias.ferias}
          onAbrirRegistros={(i, f) => navigate(`/funcionarios/documentos/vencimento-certificados?inicio=${i}&fim=${f}&auto=1&ativos=1&semAfastados=1`)}
          onAbrirCnh={(i, f) => navigate(`/funcionarios/documentos/vencimento-cnh?inicio=${i}&fim=${f}&auto=1&semAfastados=1`)}
          onVerFerias={verFerias}
          onAbrirExames={(i, f) => navigate(`/funcionarios/documentos/validade-exames?inicio=${i}&fim=${f}&auto=1&ativos=1`)}
          onAnalisarOcorrencias={() => navigate(`/funcionarios/ocorrencias/lancamento?status=A&inicio=${ocorrencias.inicio}&fim=${ocorrencias.fim}`)}
          onAnalisarPessoas={() => { const h = iso(new Date()); navigate(`/funcionarios/relatorios/afastados?inicio=${h}&fim=${h}&auto=1`) }} />
      )}
      {painelQuadro && <BlocoQuadro q={painelQuadro} onAfastados={() => { const h = iso(new Date()); navigate(`/funcionarios/relatorios/afastados?inicio=${h}&fim=${h}&auto=1`) }} />}
      {painelFerias && (
        <BlocoFerias painel={painelFerias} grupo={grupoFerias} setGrupo={setGrupoFerias}
          onAgendar={l => setModalFerias({ motcod: l.motcod, motnome: l.nome })}
          onCompleto={() => navigate('/funcionarios/documentos/controle-ferias')} />
      )}
      {painelExp && <BlocoExperiencia key={painelExp.hoje + painelExp.contratos.length} painel={painelExp} />}
      {ocorrencias && <BlocoOcorrencias d={ocorrencias} nomeEmpresa={nomeEmpresa} />}
      {painelDatas && <BlocoDatas p={painelDatas} />}
      {modalFerias && <FuncionarioFeriasModal motcod={modalFerias.motcod} motnome={modalFerias.motnome} onClose={() => { setModalFerias(null); carregar() }} />}
    </div>
  )
}
